import {Injectable} from '@angular/core';
import {Observable, throwError} from 'rxjs';
import {catchError, shareReplay} from 'rxjs/operators';
import {LpCashFlowChartData} from 'src/app/lp/shared/holdings/holding-cash-flow/LpCashFlowChartData.model';

import {HttpService} from '../http.service';
import {LpHoldingListItemReqRes} from 'src/app/lp/shared/holdings/LpHoldingListItemReqRes.model';
import {InvestmentOpportunityReqRes} from 'src/app/lp/investment-opportunity/models/investmentOpportunityReqRes.model';
import {OpportunityType} from 'src/app/lp/investment-opportunity/enums/OpportunityType.enum';
import {GpDetailsReqRes} from 'src/app/lp/models/GpDetailsReqRes.model';
import {AppSettingsService} from '../app-settings.service';
import {ProtfolioInvestment} from 'src/app/lp/lp-holdings/protfolio-investment.model';
import {ProtfolioOpportunity} from 'src/app/lp/lp-holdings/protfolio-opportunity.model';

@Injectable()
export class LpHoldingDataService {

  public readonly LP_HOLDING_BASE_ENDPOINT = 'lp/holdings';
  public readonly LP_OPPORTUNITY_BASE_ENDPOINT = 'lp/opportunity';
  public readonly LP_HOLDING_PROTFOLIO_BASE_ENDPOINT = this.appSettingsService.localEnv ? 'https://localhost:7018/api/v2/lp/protfolio/' : 'v2/lp/protfolio/';


  constructor(private http: HttpService, private appSettingsService: AppSettingsService) { }

  getProtfolioOpportunities(): Observable<ProtfolioOpportunity[]> {
    return this.http.getTyped<ProtfolioOpportunity[]>(`${this.LP_HOLDING_PROTFOLIO_BASE_ENDPOINT}opportunities`)
      .pipe(
        catchError(err => {
          return throwError(err);
        })
      );
  }

  getProtfolioActiveInvestments(): Observable<ProtfolioInvestment[]> {
    return this.http.getTyped<ProtfolioInvestment[]>(`${this.LP_HOLDING_PROTFOLIO_BASE_ENDPOINT}active`)
      .pipe(
        catchError(err => {
          return throwError(err);
        })
      );
  }

  getProtfolioPendingInvestments(): Observable<ProtfolioInvestment[]> {
    return this.http.getTyped<ProtfolioInvestment[]>(`${this.LP_HOLDING_PROTFOLIO_BASE_ENDPOINT}pending`)
      .pipe(
        catchError(err => {
          return throwError(err);
        })
      );
  }

  exportTransactions(holdingId: number): Observable<string> {
    return this.http.getTyped<string>(`${this.LP_HOLDING_BASE_ENDPOINT}/${holdingId}/transactions-export`)
      .pipe(
        catchError(err => {
          return throwError(err);
        })
      );
  }

  getNumberOfOpportunities(): Observable<number> {
    return this.http.getTyped<number>(`${this.LP_HOLDING_BASE_ENDPOINT}/opportunities-count`)
      .pipe(
        catchError(err => {
          return throwError(err);
        })
      );
  }

  getNumberOfPortfolios(): Observable<number> {
    return this.http.getTyped<number>(`${this.LP_HOLDING_BASE_ENDPOINT}/portfolios-count`)
      .pipe(
        catchError(err => {
          return throwError(err);
        })
      );
  }

  getCashFlowChartData(holdingId: number): Observable<LpCashFlowChartData> {
    return this.http.getTyped<LpCashFlowChartData>(`${this.LP_HOLDING_BASE_ENDPOINT}/${holdingId}/cash-flow-chart`)
      .pipe(
        catchError(err => {
          return throwError(err);
        })
      );
  }

  getHoldingsOpportunityCampaignData(opportunityCode: string): Observable<LpHoldingListItemReqRes[]> {
    const url = `${this.LP_OPPORTUNITY_BASE_ENDPOINT}/holdings/${opportunityCode}`;
    return this.http.getTyped<LpHoldingListItemReqRes[]>(url).pipe(
      shareReplay()
    );
  }

  getHoldingOpportunityCampaignData(opportunityCode: string, holdingId: number) {
    const url = `${this.LP_OPPORTUNITY_BASE_ENDPOINT}/campaign/${opportunityCode}/${holdingId}`;
    return this.http.getTyped<InvestmentOpportunityReqRes>(url).pipe(
      shareReplay()
    );
  }

  getGpOpportunityDetails(opportunityCode: string, opportunityType: OpportunityType): Observable<GpDetailsReqRes> {
    const url = `${this.LP_OPPORTUNITY_BASE_ENDPOINT}/gpDetails/${opportunityCode}/${opportunityType}`;
    return this.http.getTyped<GpDetailsReqRes>(url).pipe(
      shareReplay()
    );
  }

  saveInvestmentCampaignOpportunity(opportunityCode: string, holdingId: number, model: InvestmentOpportunityReqRes) {
    const url = `${this.LP_OPPORTUNITY_BASE_ENDPOINT}/campaign/${opportunityCode}/${holdingId}`;
    return this.http.putTyped<boolean>(url, model).pipe(
      shareReplay()
    );
  }


}
